import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ListChecks, ArrowRight } from 'lucide-react';
import { STATUS_META } from './rdTaskStatus';

const RD_ROLES = ['Admin', 'Engineer', 'ProductionSupervisor', 'InventoryManager'];
const URGENCY = { blocked: 0, delayed: 1, on_track: 2, advanced: 3 };

export const MyRDTasksWidget = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const allowed = RD_ROLES.includes(user?.role);

  useEffect(() => {
    if (allowed) api().get('/rd/my-tasks').then(r => setData(r.data)).catch(() => setData(null));
  }, [api, allowed]);

  if (!allowed || !data?.tasks?.length) return null;
  const top = [...data.tasks]
    .sort((a, b) => (b.is_overdue - a.is_overdue) || ((URGENCY[a.status] ?? 9) - (URGENCY[b.status] ?? 9)))
    .slice(0, 5);

  return (
    <Card className="industrial-card border-fuchsia-200" data-testid="my-rd-tasks-widget">
      <CardHeader className="border-b border-fuchsia-100 pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-fuchsia-600" />
            My R&D Tasks
            <span className="text-sm font-mono bg-fuchsia-100 text-fuchsia-700 px-2 py-0.5 rounded" data-testid="my-rd-tasks-widget-summary">
              {data.tasks.length} open · {data.overdue} overdue · {data.counts?.blocked || 0} blocked
            </span>
          </CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">Your most urgent tasks across all R&D projects.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate('/rd/my-tasks')} data-testid="my-rd-tasks-widget-open-btn">
          See all <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="p-0 divide-y divide-fuchsia-100">
        {top.map(t => {
          const meta = STATUS_META[t.status] || STATUS_META.on_track;
          return (
            <button
              key={t.id}
              onClick={() => navigate(`/rd/${t.project_id}?tab=tasks`)}
              className="w-full px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-fuchsia-50 text-left transition-colors"
              data-testid={`my-rd-tasks-widget-row-${t.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${meta.color}`}>{meta.label}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-[11px] text-slate-500 truncate">{t.project_name}</p>
                </div>
              </div>
              <span className={`text-xs shrink-0 ${t.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>
                {t.due_date ? (t.is_overdue ? `Overdue · ${t.due_date}` : t.due_date) : '—'}
              </span>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};
